/**
 * The deferred "send the replacement booking link" action.
 *
 * Behind the same ten-second countdown as every other candidate email on this
 * board. It was the last send that had none: the modal called Gmail the instant
 * the button was pressed, so there was nothing to stop and no banner to say a
 * send was in flight — on a board where every other irreversible action can be
 * taken back for ten seconds, that inconsistency is itself the bug.
 *
 * Note what "undo" can and cannot restore here. The replacement LINK is already
 * minted and the candidate's one second chance is already spent by the time this
 * runs — that happens when the new link is created, which is what makes it real
 * enough to put in an email. Stopping means the email does not go; it does not
 * un-spend the chance. The banner says so rather than implying a clean reversal.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { sendCandidateEmail } from '../../services/taOutboundApi';
import type { ExpiredInvite } from '../../services/interviewLinksApi';
import type { PendingAction } from './usePendingActions';

export function resendLinkAction({
  item,
  subject,
  body,
  gmailToken,
  onSent,
  onFailed,
}: {
  item: ExpiredInvite;
  subject: string;
  body: string;
  gmailToken: string | null;
  /** The email went — refresh the list so the row's state is honest. */
  onSent?: () => void;
  /**
   * The email did NOT go — put the draft back in front of the recruiter.
   *
   * The countdown means the failure lands ten seconds after the modal closed, so
   * without this the toast tells them to copy an email that is no longer on
   * screen, and the booking URL is unreachable because the candidate has already
   * left the list. Reopening is the only way the advice is actionable.
   */
  onFailed?: () => void;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `resend-link:${item.token}`,
    label: `Sending ${item.candidateName} a new booking link`,
    run: () => {
      void (async () => {
        try {
          const r = await sendCandidateEmail(
            {
              opportunityId: `${item.opportunityId}`,
              subject,
              body,
              // Its own kind, so the Log says what this was ("New booking link")
              // — and it counts as an invitation, so the board does not offer
              // this candidate a third one.
              kind: 'link-resend',
              roleTitle: item.roleTitle,
              stageName: item.stageName,
            },
            gmailToken
          );
          showToast({ status: 'success', description: `New booking link sent to ${r.to}.` });
          onSent?.();
        } catch (e) {
          showToast({
            status: 'error',
            description:
              e instanceof Error && e.message
                ? `Not sent: ${e.message} — the link is still valid, so you can copy the email and send it yourself.`
                : 'Not sent — the link is still valid, so you can copy the email and send it yourself.',
          });
          onFailed?.();
        }
      })();
    },
    undo: () =>
      showToast({
        status: 'info',
        description: `Stopped — no email went to ${item.candidateName}. The new link is still valid, and their second chance is still spent.`,
      }),
  };
}
